<script setup>
/* global axios */
import { ref, computed, onMounted, toRaw } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactAPI from 'dashboard/api/contacts';
import ImportMappingsAPI from 'dashboard/api/importMappings';

const props = defineProps({
  accountLabels: { type: Array, default: () => [] },
});

const emit = defineEmits(['started']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const fileInput = ref(null);

// Step management: 1 = upload, 2 = mapping
const currentStep = ref(1);

const importError = ref('');

const startImport = async () => {
  importError.value = '';
  try {
    const mappingConfig = buildMappingConfig();
    const res = await ContactAPI.importContacts(selectedFile.value, mappingConfig);
    emit('started', { importId: res.data.id });
    dialogRef.value?.close();
    resetDialog();
    useAlert('Importação iniciada! Acompanhe o progresso no canto inferior da tela.');
  } catch (err) {
    importError.value =
      err?.response?.data?.error ||
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.ERROR_MESSAGE');
  }
};

// Step 1: file
const selectedFile = ref(null);
const selectedFileName = ref('');
const isParsing = ref(false);

// Step 2: mapping data
const csvHeaders = ref([]);
const csvSampleRows = ref([]);
const columnMappings = ref({}); // { [csvCol]: 'skip' | 'std:name' | 'attr:cpf' | 'new' }
const newAttributeConfigs = ref({}); // { [csvCol]: { key, displayName, displayType } }

// Custom attribute definitions loaded from backend
const customAttributes = ref([]);

// Import label
const importLabel = ref('');

// Presets
const savedPresets = ref([]);
const showPresetSave = ref(false);
const presetName = ref('');
const isSavingPreset = ref(false);

const csvUrl = '/downloads/import-contacts-sample.csv';

const STANDARD_FIELDS = [
  { value: 'std:name', label: 'Nome / Name' },
  { value: 'std:email', label: 'E-mail' },
  { value: 'std:phone_number', label: 'Telefone / Phone' },
  { value: 'std:identifier', label: 'Identificador / Identifier' },
  { value: 'std:company', label: 'Empresa / Company' },
  { value: 'std:city', label: 'Cidade / City' },
];

const ATTRIBUTE_TYPES = ['text', 'number', 'date', 'list', 'checkbox', 'link'];

const SKY_DATA_FIELDS = [
  { value: 'sky:documentos', label: 'Documentos' },
  { value: 'sky:link_adicionais', label: 'Links Adicionais' },
  { value: 'sky:entregaveis_contratados', label: 'Entregáveis Contratados' },
  { value: 'sky:integracoes', label: 'Integrações' },
  { value: 'sky:transcricoes_markdown', label: 'Transcrições (.md)' },
];

// ─── Computed ────────────────────────────────────────────────────────────────

const confirmLabel = computed(() =>
  currentStep.value === 1
    ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.NEXT')
    : t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.IMPORT')
);

const isConfirmDisabled = computed(() =>
  currentStep.value === 1 ? !selectedFile.value || isParsing.value : false
);

const dialogTitle = computed(() =>
  currentStep.value === 2
    ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.TITLE')
    : t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TITLE')
);

// ─── File handling ────────────────────────────────────────────────────────────

const processFileName = fileName => {
  const lastDotIndex = fileName.lastIndexOf('.');
  const extension = fileName.slice(lastDotIndex);
  const baseName = fileName.slice(0, lastDotIndex);
  return baseName.length > 20 ? `${baseName.slice(0, 20)}...${extension}` : fileName;
};

const handleFileClick = () => fileInput.value?.click();

const handleFileChange = () => {
  const file = fileInput.value?.files[0];
  selectedFile.value = file || null;
  selectedFileName.value = file ? processFileName(file.name) : '';
};

const handleRemoveFile = () => {
  selectedFile.value = null;
  selectedFileName.value = '';
  if (fileInput.value) fileInput.value.value = null;
};

// ─── Preset helpers ───────────────────────────────────────────────────────────

const loadPresets = async () => {
  try {
    const res = await ImportMappingsAPI.index();
    savedPresets.value = res.data || [];
  } catch {
    savedPresets.value = [];
  }
};

const loadCustomAttributes = async () => {
  try {
    // Use the same axios instance that has auth headers configured
    const accountId = window.location.pathname.split('/')[3];
    if (!accountId) return;
    const res = await axios.get(
      `/api/v1/accounts/${accountId}/custom_attribute_definitions?attribute_model=contact_attribute`
    );
    customAttributes.value = res.data?.payload || res.data || [];
  } catch {
    customAttributes.value = [];
  }
};

const applyPreset = preset => {
  const columns = preset.mapping_config?.columns || {};
  const newMappings = {};
  const newConfigs = {};

  csvHeaders.value.forEach(header => {
    const config = columns[header];
    if (!config || config.type === 'skip') {
      newMappings[header] = 'skip';
      return;
    }
    if (config.type === 'standard') {
      newMappings[header] = `std:${config.target}`;
    } else if (config.type === 'sky_data') {
      newMappings[header] = `sky:${config.target}`;
    } else if (config.type === 'custom_attribute') {
      if (config.create_if_missing) {
        newMappings[header] = 'new';
        newConfigs[header] = {
          key: config.target,
          displayName: config.display_name || config.target,
          displayType: config.display_type || 'text',
        };
      } else {
        newMappings[header] = `attr:${config.target}`;
      }
    }
  });

  columnMappings.value = newMappings;
  newAttributeConfigs.value = newConfigs;
  importLabel.value = preset.mapping_config?.import_label || '';
};

const savePreset = async () => {
  if (!presetName.value.trim()) return;
  isSavingPreset.value = true;
  try {
    const res = await ImportMappingsAPI.create(presetName.value.trim(), buildMappingConfig());
    savedPresets.value.unshift(res.data);
    presetName.value = '';
    showPresetSave.value = false;
  } finally {
    isSavingPreset.value = false;
  }
};

const deletePreset = async preset => {
  await ImportMappingsAPI.destroy(preset.id);
  savedPresets.value = savedPresets.value.filter(p => p.id !== preset.id);
};

// ─── Mapping helpers ──────────────────────────────────────────────────────────

const slugify = str =>
  str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const buildMappingConfig = () => {
  const columns = {};
  csvHeaders.value.forEach(header => {
    const mapping = columnMappings.value[header] || 'skip';
    if (mapping === 'skip') {
      columns[header] = { type: 'skip' };
    } else if (mapping.startsWith('std:')) {
      columns[header] = { type: 'standard', target: mapping.replace('std:', '') };
    } else if (mapping.startsWith('attr:')) {
      columns[header] = { type: 'custom_attribute', target: mapping.replace('attr:', '') };
    } else if (mapping.startsWith('sky:')) {
      columns[header] = { type: 'sky_data', target: mapping.replace('sky:', '') };
    } else if (mapping === 'new') {
      const cfg = newAttributeConfigs.value[header] || {};
      columns[header] = {
        type: 'custom_attribute',
        target: cfg.key || slugify(header),
        display_name: cfg.displayName || header,
        display_type: cfg.displayType || 'text',
        create_if_missing: true,
      };
    }
  });
  const config = { columns };
  if (importLabel.value) config.import_label = importLabel.value;
  return config;
};

const getNewAttrConfig = header => {
  if (!newAttributeConfigs.value[header]) {
    newAttributeConfigs.value[header] = {
      key: slugify(header),
      displayName: header,
      displayType: 'text',
    };
  }
  return newAttributeConfigs.value[header];
};

const sampleValueFor = header => {
  const row = csvSampleRows.value[0];
  return row?.[header] || '—';
};

// ─── Step navigation ──────────────────────────────────────────────────────────

const parseError = ref('');

const parseCsvClientSide = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const text = e.target.result;
        const lines = text.split(/\r?\n/).filter(l => l.trim());
        if (!lines.length) return reject(new Error('Arquivo CSV vazio'));
        const parseCsvLine = line => {
          const result = [];
          let current = '';
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
              if (inQuotes && line[i + 1] === '"') {
                current += '"';
                i++;
              } else {
                inQuotes = !inQuotes;
              }
            } else if (ch === ',' && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else {
              current += ch;
            }
          }
          result.push(current.trim());
          return result;
        };
        const headers = parseCsvLine(lines[0]);
        const sampleRows = lines.slice(1, 4).map(line => {
          const values = parseCsvLine(line);
          return Object.fromEntries(headers.map((h, i) => [h, values[i] || '']));
        });
        resolve({ headers, sample_rows: sampleRows });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
    reader.readAsText(toRaw(file), 'UTF-8');
  });

const goToStep2 = async () => {
  isParsing.value = true;
  parseError.value = '';
  try {
    const parsed = await parseCsvClientSide(selectedFile.value);
    csvHeaders.value = parsed.headers || [];
    csvSampleRows.value = parsed.sample_rows || [];

    // Auto-match columns by name
    const mappings = {};
    csvHeaders.value.forEach(header => {
      const lc = slugify(header);
      const stdMatch = STANDARD_FIELDS.find(f => f.value === `std:${lc}`);
      if (stdMatch) {
        mappings[header] = stdMatch.value;
        return;
      }
      const skyMatch = SKY_DATA_FIELDS.find(f => f.value === `sky:${lc}`);
      if (skyMatch) {
        mappings[header] = skyMatch.value;
        return;
      }
      const attrMatch = customAttributes.value.find(
        a =>
          a.attribute_key === lc ||
          a.attribute_display_name?.toLowerCase() === header.toLowerCase()
      );
      mappings[header] = attrMatch ? `attr:${attrMatch.attribute_key}` : 'skip';
    });
    columnMappings.value = mappings;
    currentStep.value = 2;
  } catch (err) {
    parseError.value =
      err?.response?.data?.error ||
      err?.message ||
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.ERROR_MESSAGE');
  } finally {
    isParsing.value = false;
  }
};

const handleConfirm = async () => {
  if (currentStep.value === 1) {
    await goToStep2();
  } else if (currentStep.value === 2) {
    await startImport();
  }
};

const resetDialog = () => {
  currentStep.value = 1;
  selectedFile.value = null;
  selectedFileName.value = '';
  csvHeaders.value = [];
  csvSampleRows.value = [];
  columnMappings.value = {};
  newAttributeConfigs.value = {};
  showPresetSave.value = false;
  presetName.value = '';
  importLabel.value = '';
  importError.value = '';
  if (fileInput.value) fileInput.value.value = null;
};

onMounted(async () => {
  store.dispatch('labels/get');
  await Promise.all([loadPresets(), loadCustomAttributes()]);
});

defineExpose({ dialogRef, resetDialog });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="dialogTitle"
    :confirm-button-label="confirmLabel"
    :is-loading="isParsing"
    :disable-confirm-button="isConfirmDisabled"
    @confirm="handleConfirm"
  >
    <!-- Single #description slot with conditional content -->
    <template #description>
      <!-- Step 1 description -->
      <p v-if="currentStep === 1" class="mb-0 text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DESCRIPTION') }}
        <a
          :href="csvUrl"
          target="_blank"
          rel="noopener noreferrer"
          download="import-contacts-sample.csv"
          class="text-n-blue-11"
        >
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DOWNLOAD_LABEL') }}
        </a>
      </p>
      <!-- Step 2 description -->
      <div v-else-if="currentStep === 2" class="flex items-center justify-between gap-2">
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.DESCRIPTION') }}
        </p>
        <Button
          :label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.BACK')"
          icon="i-lucide-arrow-left"
          color="slate"
          variant="ghost"
          size="sm"
          type="button"
          class="!w-fit shrink-0"
          @click="currentStep = 1"
        />
      </div>
    </template>

    <!-- ── Step 1: Upload ─────────────────────────────────────────────────── -->
    <div v-if="currentStep === 1" class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <label class="text-sm text-n-slate-12 whitespace-nowrap">
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABEL') }}
        </label>
        <div class="flex items-center justify-between w-full gap-2">
          <span v-if="selectedFile" class="text-sm text-n-slate-12">
            {{ selectedFileName }}
          </span>
          <Button
            v-if="!selectedFile"
            :label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CHOOSE_FILE')"
            icon="i-lucide-upload"
            color="slate"
            variant="ghost"
            size="sm"
            type="button"
            class="!w-fit"
            @click="handleFileClick"
          />
          <div v-else class="flex items-center gap-1">
            <Button
              :label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CHANGE')"
              color="slate"
              variant="ghost"
              size="sm"
              type="button"
              @click="handleFileClick"
            />
            <div class="w-px h-3 bg-n-strong" />
            <Button
              icon="i-lucide-trash"
              color="slate"
              variant="ghost"
              size="sm"
              type="button"
              @click="handleRemoveFile"
            />
          </div>
        </div>
      </div>
      <p v-if="isParsing" class="text-xs text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.ANALYZING') }}
      </p>
      <p v-if="parseError" class="text-xs text-red-500">{{ parseError }}</p>
    </div>

    <!-- ── Step 2: Column mapping ─────────────────────────────────────────── -->
    <div v-else-if="currentStep === 2">

      <!-- Preset bar -->
      <div class="flex items-center gap-2 mb-3">
        <select
          class="text-sm border border-n-weak rounded px-2 py-1 bg-n-background text-n-slate-12 flex-1"
          @change="
            e => {
              const p = savedPresets.find(x => String(x.id) === e.target.value);
              if (p) applyPreset(p);
              e.target.value = '';
            }
          "
        >
          <option value="">
            {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.PRESET_LOAD') }}
          </option>
          <option v-for="preset in savedPresets" :key="preset.id" :value="preset.id">
            {{ preset.name }}
          </option>
        </select>

        <Button
          v-if="!showPresetSave"
          :label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.PRESET_SAVE')"
          icon="i-lucide-save"
          color="slate"
          variant="ghost"
          size="sm"
          type="button"
          class="!w-fit shrink-0"
          @click="showPresetSave = true"
        />
      </div>

      <!-- Save preset form -->
      <div v-if="showPresetSave" class="flex items-center gap-2 mb-3">
        <input
          v-model="presetName"
          type="text"
          :placeholder="
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.PRESET_NAME_PLACEHOLDER')
          "
          class="flex-1 text-sm border border-n-weak rounded px-2 py-1 bg-n-background text-n-slate-12"
          @keyup.enter="savePreset"
        />
        <Button
          :label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.PRESET_SAVE_BTN')"
          color="slate"
          size="sm"
          type="button"
          :is-loading="isSavingPreset"
          class="!w-fit shrink-0"
          @click="savePreset"
        />
        <Button
          icon="i-lucide-x"
          color="slate"
          variant="ghost"
          size="sm"
          type="button"
          class="!w-fit"
          @click="showPresetSave = false"
        />
      </div>

      <!-- Mapping table -->
      <div class="overflow-auto max-h-80">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-11 border-b border-n-weak">
              <th class="pb-2 pr-3 font-medium w-1/3">
                {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.CSV_COLUMN') }}
              </th>
              <th class="pb-2 pr-3 font-medium w-1/4">
                {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.SAMPLE_VALUE') }}
              </th>
              <th class="pb-2 font-medium">
                {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.TARGET_FIELD') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="header in csvHeaders" :key="header">
              <tr class="border-b border-n-alpha-2">
                <td class="py-2 pr-3 text-n-slate-12 font-mono text-xs">{{ header }}</td>
                <td class="py-2 pr-3 text-n-slate-11 text-xs truncate max-w-24">
                  {{ sampleValueFor(header) }}
                </td>
                <td class="py-2">
                  <select
                    :value="columnMappings[header] || 'skip'"
                    class="w-full text-xs border border-n-weak rounded px-2 py-1 bg-n-background text-n-slate-12"
                    @change="e => (columnMappings[header] = e.target.value)"
                  >
                    <option value="skip">
                      {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.SKIP') }}
                    </option>
                    <optgroup
                      :label="
                        t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.STANDARD_GROUP')
                      "
                    >
                      <option
                        v-for="field in STANDARD_FIELDS"
                        :key="field.value"
                        :value="field.value"
                      >
                        {{ field.label }}
                      </option>
                    </optgroup>
                    <optgroup label="Sky Data (Sublistas)">
                      <option
                        v-for="field in SKY_DATA_FIELDS"
                        :key="field.value"
                        :value="field.value"
                      >
                        {{ field.label }}
                      </option>
                    </optgroup>
                    <optgroup
                      v-if="customAttributes.length"
                      :label="
                        t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.CUSTOM_GROUP')
                      "
                    >
                      <option
                        v-for="attr in customAttributes"
                        :key="attr.attribute_key"
                        :value="`attr:${attr.attribute_key}`"
                      >
                        {{ attr.attribute_display_name }}
                      </option>
                    </optgroup>
                    <option value="new">
                      {{
                        t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.NEW_ATTRIBUTE')
                      }}
                    </option>
                  </select>
                </td>
              </tr>

              <!-- Inline new attribute config -->
              <tr v-if="columnMappings[header] === 'new'" :key="`${header}-cfg`">
                <td colspan="3" class="pb-2 pt-0">
                  <div class="flex gap-2 bg-n-alpha-1 rounded p-2">
                    <input
                      v-model="getNewAttrConfig(header).key"
                      type="text"
                      :placeholder="
                        t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.NEW_ATTR_KEY')
                      "
                      class="flex-1 text-xs border border-n-weak rounded px-2 py-1 bg-n-background text-n-slate-12"
                    />
                    <input
                      v-model="getNewAttrConfig(header).displayName"
                      type="text"
                      :placeholder="
                        t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.NEW_ATTR_NAME')
                      "
                      class="flex-1 text-xs border border-n-weak rounded px-2 py-1 bg-n-background text-n-slate-12"
                    />
                    <select
                      v-model="getNewAttrConfig(header).displayType"
                      class="text-xs border border-n-weak rounded px-2 py-1 bg-n-background text-n-slate-12"
                    >
                      <option v-for="type in ATTRIBUTE_TYPES" :key="type" :value="type">
                        {{
                          t(
                            `CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPPING.TYPES.${type}`
                          )
                        }}
                      </option>
                    </select>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Import label -->
      <div class="mt-3 pt-3 border-t border-n-weak flex items-center gap-3">
        <label class="text-xs font-medium text-n-slate-11 shrink-0">
          Aplicar etiqueta aos contatos importados:
        </label>
        <select
          v-model="importLabel"
          class="flex-1 text-xs border border-n-weak rounded px-2 py-1 bg-n-background text-n-slate-12"
        >
          <option value="">Nenhuma</option>
          <option
            v-for="label in accountLabels"
            :key="label.title"
            :value="label.title"
          >
            {{ label.title }}
          </option>
        </select>
      </div>

      <!-- Saved presets chips (for deletion) -->
      <div v-if="savedPresets.length" class="mt-3 border-t border-n-weak pt-2">
        <div class="flex flex-wrap gap-2">
          <div
            v-for="preset in savedPresets"
            :key="preset.id"
            class="flex items-center gap-1 bg-n-alpha-1 rounded px-2 py-1 text-xs text-n-slate-12"
          >
            <span>{{ preset.name }}</span>
            <button
              class="text-n-slate-11 hover:text-red-500 ml-1 leading-none"
              @click="deletePreset(preset)"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    </div>

    <p v-if="importError" class="text-xs text-red-500 mt-2">{{ importError }}</p>

    <input
      ref="fileInput"
      type="file"
      accept="text/csv"
      class="hidden"
      @change="handleFileChange"
    />
  </Dialog>
</template>
